import { useState } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircle, Send, Mail, Link2, Check, Facebook } from "lucide-react";

const SHARE_TEXT =
  "Join the Ashley Falls PTA membership drive — fund amazing school programs and show our students their community has their back. Your membership matters!";

export const ShareBar = () => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const msg = encodeURIComponent(`${SHARE_TEXT} ${url}`);

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Ashley Falls PTA", text: SHARE_TEXT, url });
      } catch (e) {}
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pill =
    "inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs font-bold uppercase tracking-widest transition-shadow focus:ring-2 focus:ring-pink-500 focus:outline-none";

  return (
    <section data-testid="share-section" className="relative bg-slate-950 py-16 overflow-hidden">
      <div className="neon-hairline absolute top-0 left-0 right-0" />
      <div className="absolute -top-20 left-1/4 w-[350px] h-[350px] rounded-full bg-pink-500/10 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-20 right-1/4 w-[350px] h-[350px] rounded-full bg-cyan-400/10 blur-[100px] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-400">Spread the word</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
            Know another <span className="text-pink-500">Eagle family?</span>
          </h2>
          <p className="mt-3 text-sm md:text-base font-medium text-slate-400 max-w-xl mx-auto">
            One tap sends the drive to friends, class group chats, and grandparents —
            every share grows the flock.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={nativeShare}
              data-testid="share-native-button"
              className={`${pill} bg-pink-500 text-white hover:shadow-[0_0_20px_rgba(255,0,127,0.6)]`}
            >
              <Share2 size={15} /> Share
            </button>
            <a
              href={`sms:?&body=${msg}`}
              data-testid="share-sms-button"
              className={`${pill} bg-slate-900 text-cyan-300 border border-slate-700 hover:shadow-[0_0_16px_rgba(0,240,255,0.4)]`}
            >
              <MessageCircle size={15} /> Text
            </a>
            <a
              href={`https://wa.me/?text=${msg}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-whatsapp-button"
              className={`${pill} bg-slate-900 text-emerald-300 border border-slate-700 hover:shadow-[0_0_16px_rgba(52,211,153,0.4)]`}
            >
              <Send size={15} /> WhatsApp
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-facebook-button"
              className={`${pill} bg-slate-900 text-blue-300 border border-slate-700 hover:shadow-[0_0_16px_rgba(96,165,250,0.4)]`}
            >
              <Facebook size={15} /> Facebook
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("Join the Ashley Falls PTA drive!")}&body=${msg}`}
              data-testid="share-email-button"
              className={`${pill} bg-slate-900 text-yellow-300 border border-slate-700 hover:shadow-[0_0_16px_rgba(255,230,0,0.35)]`}
            >
              <Mail size={15} /> Email
            </a>
            <button
              onClick={copyLink}
              data-testid="share-copy-button"
              className={`${pill} bg-slate-900 text-fuchsia-300 border border-slate-700 hover:shadow-[0_0_16px_rgba(217,70,239,0.4)]`}
            >
              {copied ? <Check size={15} /> : <Link2 size={15} />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
